import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import toast from 'react-hot-toast';
import { Truck, Plus, Search, Package, ChevronRight, Loader2, Filter, RefreshCw, Lock, Mail } from 'lucide-react';
import { CreateLoadDialog } from '../components/loads/CreateLoadDialog';
import { LoadDetailDrawer } from '../components/loads/LoadDetailDrawer';
import { useLocation } from 'wouter';
import { useAuthStore } from '../hooks/useAuth';

const STATUS_COLORS: Record<string, string> = {
    draft: 'bg-slate-500/15 text-slate-400',
    ready: 'bg-blue-500/15 text-blue-400',
    scheduled: 'bg-indigo-500/15 text-indigo-400',
    in_transit: 'bg-amber-500/15 text-amber-400',
    arrived: 'bg-emerald-500/15 text-emerald-400',
    unloading: 'bg-violet-500/15 text-violet-400',
    completed: 'bg-slate-200 dark:bg-slate-700/30 text-slate-500',
    cancelled: 'bg-red-500/15 text-red-400',
};

const STATUS_OPTIONS = [
    { value: 'all', label: 'All Status' },
    { value: 'draft', label: 'Draft' },
    { value: 'ready', label: 'Ready' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'in_transit', label: 'In Transit' },
    { value: 'arrived', label: 'Arrived' },
    { value: 'unloading', label: 'Unloading' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
];

export function Loads() {
    const { user } = useAuthStore();
    const isOperationsEntitled = user?.effectiveTier === 'operations';
    const [loads, setLoads] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [showCreate, setShowCreate] = useState(false);
    const [selectedLoadId, setSelectedLoadId] = useState<string | null>(null);
    const [, setLocation] = useLocation();

    const fetchLoads = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.getLoads({
                status: statusFilter !== 'all' ? statusFilter : undefined,
                type: typeFilter !== 'all' ? typeFilter : undefined,
                q: searchTerm || undefined,
            });
            setLoads(res.loads);
        } catch (err: any) {
            toast.error(err.message || 'Failed to load');
        } finally {
            setLoading(false);
        }
    }, [statusFilter, typeFilter, searchTerm]);

    useEffect(() => {
        fetchLoads();
    }, [fetchLoads]);

    // Search debounce
    const [debouncedSearch, setDebouncedSearch] = useState('');
    useEffect(() => {
        const t = setTimeout(() => setSearchTerm(debouncedSearch), 300);
        return () => clearTimeout(t);
    }, [debouncedSearch]);

    // Stats
    const draftCount = loads.filter(l => l.status === 'draft').length;
    const readyCount = loads.filter(l => l.status === 'ready').length;
    const activeCount = loads.filter(l => ['scheduled', 'in_transit', 'arrived', 'unloading'].includes(l.status)).length;
    const completedCount = loads.filter(l => l.status === 'completed').length;

    return (
        <div className="min-h-screen p-6 max-w-7xl mx-auto space-y-6 animate-fade-in pb-20">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight flex items-center gap-3">
                        <div className="p-2 bg-cyan-500/10 rounded-xl">
                            <Truck className="text-cyan-500" size={26} />
                        </div>
                        Loads
                    </h1>
                    <p className="text-slate-500 mt-1 ml-14">Manage truck/trailer movements and PO groupings</p>
                </div>
                {isOperationsEntitled && (
                    <button
                        onClick={() => setShowCreate(true)}
                        className="px-5 py-2.5 rounded-xl text-sm font-bold bg-cyan-600 hover:bg-cyan-500 text-white transition-all flex items-center gap-2 shadow-lg shadow-cyan-500/20"
                    >
                        <Plus size={16} /> New Load
                    </button>
                )}
            </div>

            {!isOperationsEntitled && (
                <div
                    data-testid="loads-operations-upgrade-banner"
                    className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                >
                    <div className="space-y-1">
                        <div className="flex items-center gap-2">
                            <Lock className="w-4 h-4 text-amber-500 shrink-0" />
                            <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-500 border border-amber-500/30">
                                Operations Plan Feature
                            </span>
                            <span className="text-xs font-semibold text-slate-500">
                                Starter Plan ($99/mo) — Read-Only View
                            </span>
                        </div>
                        <h2 className="text-base font-bold text-slate-900 dark:text-white">
                            Upgrade to Operations ($199 USD/month per facility) for Consolidated Load Management
                        </h2>
                        <p className="text-xs text-slate-600 dark:text-slate-400 max-w-2xl">
                            Creating loads, grouping multiple POs onto a single trailer movement, and scheduling consolidated load appointments require the Operations plan. Existing historical loads remain viewable below in read-only mode, and standard single- or multi-PO appointments remain included in Starter.
                        </p>
                    </div>
                    <a
                        href="mailto:support@baywhiz.com?subject=Upgrade%20to%20BayWhiz%20Operations%20Plan"
                        className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold text-xs transition-colors shrink-0"
                    >
                        <Mail className="w-4 h-4" />
                        Contact support@baywhiz.com to Upgrade
                    </a>
                </div>
            )}

            {/* Stats Strip */}
            <div className="grid grid-cols-4 gap-4">
                {[
                    { label: 'Draft', count: draftCount, color: 'text-slate-400', bg: 'bg-slate-500/10' },
                    { label: 'Ready', count: readyCount, color: 'text-blue-400', bg: 'bg-blue-500/10' },
                    { label: 'Active', count: activeCount, color: 'text-violet-400', bg: 'bg-violet-500/10' },
                    { label: 'Completed', count: completedCount, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
                ].map(stat => (
                    <div key={stat.label} className={`${stat.bg} rounded-xl p-4 border border-slate-200 dark:border-slate-700/50`}>
                        <div className={`text-2xl font-bold font-mono ${stat.color}`}>{stat.count}</div>
                        <div className="text-xs text-slate-500 uppercase font-medium mt-0.5">{stat.label}</div>
                    </div>
                ))}
            </div>

            {/* Filters */}
            <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                    <input
                        type="text"
                        value={debouncedSearch}
                        onChange={(e) => setDebouncedSearch(e.target.value)}
                        placeholder="Search loads, carriers, trailers..."
                        className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none transition-all"
                    />
                </div>
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white appearance-none pr-8 outline-none"
                >
                    {STATUS_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
                <select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    className="px-3 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm text-slate-900 dark:text-white appearance-none pr-8 outline-none"
                >
                    <option value="all">All Types</option>
                    <option value="inbound">Inbound</option>
                    <option value="outbound">Outbound</option>
                </select>
                <button
                    onClick={fetchLoads}
                    className="p-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-400 hover:text-cyan-500 hover:border-cyan-500/30 transition-all"
                >
                    <RefreshCw size={16} />
                </button>
            </div>

            {/* Table */}
            <div className="bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700/50 rounded-2xl overflow-hidden shadow-sm">
                {/* Table Header */}
                <div className="grid grid-cols-[1fr_100px_120px_80px_80px_100px_40px] gap-4 px-5 py-3 bg-slate-50 dark:bg-slate-800/30 border-b border-slate-200 dark:border-slate-700/50 text-xs font-bold text-slate-500 uppercase tracking-wider">
                    <div>Load</div>
                    <div>Status</div>
                    <div>Carrier</div>
                    <div className="text-right">POs</div>
                    <div className="text-right">Pallets</div>
                    <div className="text-right">Weight</div>
                    <div></div>
                </div>

                {/* Rows */}
                {loading ? (
                    <div className="flex items-center justify-center py-16">
                        <Loader2 className="animate-spin text-cyan-500" size={24} />
                    </div>
                ) : loads.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                        <div className="bg-slate-100 dark:bg-slate-800 p-4 rounded-full mb-4">
                            <Truck className="text-slate-400" size={28} />
                        </div>
                        <h3 className="text-slate-700 dark:text-slate-300 font-bold mb-1">No loads found</h3>
                        <p className="text-slate-500 text-sm max-w-xs">
                            {isOperationsEntitled
                                ? 'Create a load to group POs for a truck/trailer movement.'
                                : 'No historical loads recorded for this facility.'}
                        </p>
                        {isOperationsEntitled && (
                            <button
                                onClick={() => setShowCreate(true)}
                                className="mt-4 px-4 py-2 text-sm font-bold text-cyan-500 hover:text-cyan-400 border border-cyan-500/30 rounded-lg hover:bg-cyan-500/10 transition-all"
                            >
                                <Plus size={14} className="inline mr-1" /> Create First Load
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="divide-y divide-slate-100 dark:divide-slate-800/50">
                        {loads.map(load => {
                            const statusClass = STATUS_COLORS[load.status] || STATUS_COLORS.draft;
                            return (
                                <button
                                    key={load.id}
                                    onClick={() => setSelectedLoadId(load.id)}
                                    className="w-full grid grid-cols-[1fr_100px_120px_80px_80px_100px_40px] gap-4 px-5 py-4 items-center text-left hover:bg-slate-50 dark:hover:bg-white/5 transition-colors group"
                                >
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="font-mono font-bold text-slate-900 dark:text-white text-sm">{load.loadNumber}</span>
                                            <span className="text-xs text-slate-400">
                                                {load.type === 'inbound' ? '📥' : '📤'}
                                            </span>
                                        </div>
                                        {load.trailerNumber && (
                                            <div className="text-xs text-slate-400 mt-0.5">
                                                Trailer: {load.trailerNumber}
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${statusClass}`}>
                                            {(load.status || 'draft').replace('_', ' ')}
                                        </span>
                                    </div>
                                    <div className="text-sm text-slate-600 dark:text-slate-300 truncate">
                                        {load.carrierName || '—'}
                                    </div>
                                    <div className="text-right text-sm font-mono font-bold text-slate-900 dark:text-white">
                                        {load.poCount || 0}
                                    </div>
                                    <div className="text-right text-sm font-mono text-slate-600 dark:text-slate-300">
                                        {load.totalPallets || 0}
                                    </div>
                                    <div className="text-right text-sm font-mono text-slate-500">
                                        {(load.totalWeight || 0).toLocaleString()}
                                    </div>
                                    <div className="text-right">
                                        <ChevronRight size={16} className="text-slate-300 group-hover:text-cyan-400 transition-colors" />
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Create Dialog */}
            {isOperationsEntitled && (
                <CreateLoadDialog
                    open={showCreate}
                    onClose={() => setShowCreate(false)}
                    onCreated={() => {
                        fetchLoads();
                        if (new URLSearchParams(window.location.search).get('from') === 'onboarding') {
                            setLocation('/dashboard?from=onboarding');
                        }
                    }}
                />
            )}

            {/* Detail Drawer */}
            <LoadDetailDrawer
                loadId={selectedLoadId}
                onClose={() => setSelectedLoadId(null)}
                onUpdated={fetchLoads}
            />
        </div>
    );
}
